"use client";

import { PriorityTicketsInfo } from "@/components/features/automatic-streak/PriorityTicketsInfo";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { SkeletonElement } from "@/components/ui/skeleton";
import { arenaUrlWithUtm } from "@/constants/airdrop-farming";
import { useDevice } from "@/hooks";
import { usePriorityTickets } from "@/hooks/automatic-streak";
import { cn } from "@/lib/utils";
import { Zap } from "lucide-react";

/** Autopilot priority-ticket balance (earned from paid Arena competitions) + Arena CTA. */
export const PriorityTicketsCard = () => {
  const { isMobile } = useDevice();
  const {
    isFeatureEnabled,
    totalTickets: priorityTickets,
    isLoading: isLoadingPriorityTickets,
    isError: isPriorityTicketsError,
  } = usePriorityTickets();

  if (!isFeatureEnabled) return null;

  return (
    <InfoCard
      title={
        <span className="flex items-center gap-2">
          Priority Tickets
          <PriorityTicketsInfo />
        </span>
      }
      padding={isMobile ? "none" : "md"}
      withBackground={!isMobile}
      withBorder={!isMobile}
      gap="lg"
      className={cn("flex min-w-0 flex-col")}
      contentClassName="flex flex-1 flex-col justify-between gap-5"
      titleClassName="text-lg font-semibold text-cede-text-primary"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-medal-gold/15">
            <Zap className="h-6 w-6 text-medal-gold" strokeWidth={1.5} fill="currentColor" />
          </div>
          {isLoadingPriorityTickets ? (
            <SkeletonElement className="h-[60px] w-[60px] rounded-lg" />
          ) : (
            // Error must not read as an empty balance — show a placeholder instead of 0.
            <span className="text-5xl font-bold text-medal-gold">{isPriorityTicketsError ? "—" : priorityTickets}</span>
          )}
        </div>
        {isPriorityTicketsError ? (
          <p className="text-xs text-cede-error">Couldn&apos;t load your ticket balance. Try again later.</p>
        ) : (
          <p className="text-xs text-cede-text-secondary">
            Each ticket puts you first in line for one epoch when autopilot slots are picked.
          </p>
        )}
      </div>

      <Button
        variant="secondary"
        size="medium"
        onClick={() => window.open(arenaUrlWithUtm("cedehub-priority-ticket"), "_blank", "noopener,noreferrer")}
        className="w-full"
      >
        Earn Tickets in Arena
      </Button>
    </InfoCard>
  );
};
